import React, { useEffect, useRef, useState } from 'react';
import { Modal, Slider, Checkbox } from '@mui/material';
import { EnvelopeSimpleIcon, XIcon, WarningCircleIcon } from '@phosphor-icons/react';
import '../styles/TuftedRugModal.css';
import { useLanguage } from '../i18n/LanguageContext';

const hexToRgb = (hex) => {
    const cleaned = hex.replace('#', '');
    const value = cleaned.length === 3
        ? cleaned
              .split('')
              .map((channel) => channel + channel)
              .join('')
        : cleaned;
    const intValue = parseInt(value, 16);
    return [
        (intValue >> 16) & 255,
        (intValue >> 8) & 255,
        intValue & 255
    ];
};

const pivotRgb = (value) => {
    const normalized = value / 255;
    return normalized > 0.04045
        ? Math.pow((normalized + 0.055) / 1.055, 2.4)
        : normalized / 12.92;
};

const rgbToLab = (r, g, b) => {
    const rLinear = pivotRgb(r);
    const gLinear = pivotRgb(g);
    const bLinear = pivotRgb(b);
    const x = (rLinear * 0.4124 + gLinear * 0.3576 + bLinear * 0.1805) / 0.95047;
    const y = (rLinear * 0.2126 + gLinear * 0.7152 + bLinear * 0.0722) / 1.0;
    const z = (rLinear * 0.0193 + gLinear * 0.1192 + bLinear * 0.9505) / 1.08883;
    const pivotXyz = (value) =>
        value > 0.008856 ? Math.cbrt(value) : (7.787 * value) + 16 / 116;
    const fx = pivotXyz(x);
    const fy = pivotXyz(y);
    const fz = pivotXyz(z);
    const l = Math.max(0, 116 * fy - 16);
    const a = 500 * (fx - fy);
    const bValue = 200 * (fy - fz);
    return [l, a, bValue];
};

const yarnPalette = [
    { name: 'AMANDE', hex: '#adc8b5' },
    { name: 'ARMY', hex: '#4a4936' },
    { name: 'AUBEPINE', hex: '#eaacb4' },
    { name: 'BLANC', hex: '#ffffff' },
    { name: 'BRUN AUBURN', hex: '#9d3e0c' },
    { name: 'BURGUNDY', hex: '#800020' },
    { name: 'CEDRE', hex: '#1e4230' },
    { name: 'CORAIL', hex: '#e73e01' },
    { name: 'CRAIE', hex: '#dfd7cc' },
    { name: 'ECRU', hex: '#cdb891' },
    { name: 'ECUREUIL', hex: '#db611b' },
    { name: 'FLANELLE', hex: '#636162' },
    { name: 'GAZON', hex: '#4d5d0f' },
    { name: 'GIVRE', hex: '#d9dee0' },
    { name: 'GLACON', hex: '#afc9ce' },
    { name: 'JACINTHE', hex: '#a883ba' },
    { name: 'JEANS', hex: '#2b4f70' },
    { name: 'LIN', hex: '#af9f99' },
    { name: 'MARINE', hex: '#334159' },
    { name: 'MIRABELLE', hex: '#f3be67' },
    { name: 'NOIR', hex: '#000000' },
    { name: 'NOISETTE', hex: '#955628' },
    { name: 'NUEE', hex: '#8eacc3' },
    { name: 'OCEAN', hex: '#063d7f' },
    { name: 'PAVOT', hex: '#ab1514' },
    { name: 'PETUNIA', hex: '#d0445b' },
    { name: 'ROUGE', hex: '#f00020' },
    { name: 'SABLE', hex: '#f0ddc5' },
    { name: 'VERONESE', hex: '#009b7d' },
    { name: 'VERT PLUME', hex: '#67989d' },
    { name: 'VIEUX ROSE', hex: '#c8767d' }
];

const paletteWithRgb = yarnPalette.map((color) => ({
    ...color,
    rgb: hexToRgb(color.hex)
}));

const paletteWithLab = paletteWithRgb.map((color) => {
    const lab = rgbToLab(...color.rgb);
    const hue = (Math.atan2(lab[2], lab[1]) * 180) / Math.PI;
    return {
        ...color,
        lab,
        chroma: Math.hypot(lab[1], lab[2]),
        hue: (hue + 360) % 360
    };
});

const neutralPaletteNames = new Set(['BLANC', 'NOIR']);
const neutralPaletteIndices = paletteWithLab.reduce((acc, color, index) => {
    if (neutralPaletteNames.has(color.name)) {
        acc.push(index);
    }
    return acc;
}, []);
const neutralPaletteSet = new Set(neutralPaletteIndices);
const chromaScale = 120;
const hueBinSize = 30;
const lightnessWeight = 0.95;
const hueWeight = 0.45;
const hueDampen = 30;
const maxPerHueBin = 3;

const maxSize = 75;
const maxYarns = 34;
const maxHours = 10;
const formName = 'tufting-estimate';
const colorLimit = 10;
const neutralColorRatio = 0.003;
const maxPreviewSize = 200;
const maxRenderSize = 480;

const unitPrices = {
    Yarns: 3,
    Cloth: 10,
    'Rug Glue': 12.9,
    'Anti-Slip Fabric': 21.99,
    'Spray Glue': 15,
    'Glue Gun': 0.25,
    'Tufting Gun Costs': 199.99,
    'Mower Costs': 55.99,
    'Working Hours': 5,
    'Edge Cover Strip': 17
};

const sliderSx = {
    color: 'var(--accent)',
    height: 6,
    padding: '14px 0',
    '& .MuiSlider-thumb': {
        width: 16,
        height: 16,
        backgroundColor: '#fff',
        border: '2px solid var(--accent)',
        boxShadow: '0 6px 16px rgba(31, 42, 90, 0.2)'
    },
    '& .MuiSlider-track': {
        border: 'none'
    },
    '& .MuiSlider-rail': {
        opacity: 0.3,
        color: 'var(--accent)'
    }
};

const checkboxSx = {
    color: 'var(--accent-2)',
    '&.Mui-checked': {
        color: 'var(--accent)'
    }
};

const TuftedRugModal = ({ open, onClose, onSuccess }) => {
    const { t, language } = useLanguage();
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [isShipping, setIsShipping] = useState(false);
    const [isMiror, setIsMiror] = useState(false);
    const [isWallMount, setIsWallMount] = useState(false);
    const [referenceFile, setReferenceFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [renderUrl, setRenderUrl] = useState('');
    const [renderStatus, setRenderStatus] = useState('idle');
    const [renderMessage, setRenderMessage] = useState('');
    const [paletteColors, setPaletteColors] = useState([]);
    const [previewColorCount, setPreviewColorCount] = useState(6);
    const previewRequestId = useRef(0);
    const renderBufferRef = useRef(null);
    const [paletteSelection, setPaletteSelection] = useState(null);
    const [email, setEmail] = useState('');
    const [comment, setComment] = useState('');
    const [showComment, setShowComment] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('idle');
    const dimensions = t('modal.dimensions');
    const options = t('modal.options');
    const breakdown = t('modal.breakdown');
    const cta = t('modal.cta');
    const priceLabels = t('modal.priceLabels');
    const preview = t('modal.options.preview');
    const subjectLine = email
        ? t('modal.formSubject', { email })
        : t('modal.formSubjectFallback');
    const paletteSummary = paletteColors
        .map((color) => `${color.name} ${color.hex}`)
        .join(', ');
    const availablePalette = paletteWithLab.filter(
        (color) =>
            !paletteColors.some((selected) => selected.hex === color.hex)
    );

    const handleWidthChange = (event, newValue) => {
        setWidth(newValue);
    };

    const handleHeightChange = (event, newValue) => {
        setHeight(newValue);
    };

    const handleShippingChange = (event) => {
        setIsShipping(event.target.checked);
    };

    const handleMirorChange = (event) => {
        setIsMiror(event.target.checked);
    };

    const handleWallMountChange = (event) => {
        setIsWallMount(event.target.checked);
    };

    const handleReferenceChange = (event) => {
        const [file] = event.target.files || [];
        setReferenceFile(file || null);
        setSubmitStatus('idle');
        setRenderStatus('idle');
        setRenderMessage('');
        setRenderUrl('');
        setPaletteColors([]);
        renderBufferRef.current = null;
        setPaletteSelection(null);
        setComment('');
        setShowComment(false);
        const nextUrl = file ? URL.createObjectURL(file) : '';
        setPreviewUrl((prev) => {
            if (prev) {
                URL.revokeObjectURL(prev);
            }
            return nextUrl;
        });
        if (nextUrl) {
            handleGeneratePreview(nextUrl, previewColorCount);
        }
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
        setSubmitStatus('idle');
    };

    const handleCommentChange = (event) => {
        setComment(event.target.value);
        setSubmitStatus('idle');
    };

    const handlePreviewColorChange = (event, newValue) => {
        const nextValue = Array.isArray(newValue) ? newValue[0] : newValue;
        setPreviewColorCount(nextValue);
        if (previewUrl) {
            handleGeneratePreview(previewUrl, nextValue);
        }
    };

    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    };

    const getScaledSize = (widthValue, heightValue, maxSize) => {
        if (widthValue <= maxSize && heightValue <= maxSize) {
            return { width: widthValue, height: heightValue };
        }
        const ratio = widthValue / heightValue;
        if (ratio >= 1) {
            return { width: maxSize, height: Math.round(maxSize / ratio) };
        }
        return { width: Math.round(maxSize * ratio), height: maxSize };
    };

    const dataUrlToBlob = (dataUrl) => {
        if (!dataUrl.startsWith('data:')) {
            return null;
        }
        const [header, data] = dataUrl.split(',');
        if (!header || !data) {
            return null;
        }
        const match = header.match(/data:(.*?);base64/);
        const mime = match ? match[1] : 'image/jpeg';
        const binary = atob(data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: mime });
    };

    const getNearestPaletteIndex = (r, g, b) => {
        let bestIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;
        const [lValue, aValue, bValue] = rgbToLab(r, g, b);
        const chroma = Math.hypot(aValue, bValue);
        const hue = chroma < 0.5
            ? 0
            : (Math.atan2(bValue, aValue) * 180) / Math.PI + 360;
        const normalizedHue = hue % 360;
        const hueFactor = chroma / (chroma + hueDampen);
        for (let i = 0; i < paletteWithLab.length; i += 1) {
            const [pl, pa, pb] = paletteWithLab[i].lab;
            const hueDiff = Math.min(
                Math.abs(normalizedHue - paletteWithLab[i].hue),
                360 - Math.abs(normalizedHue - paletteWithLab[i].hue)
            );
            const dl = (lValue - pl) * lightnessWeight;
            const da = aValue - pa;
            const db = bValue - pb;
            const huePenalty = Math.pow(hueDiff * hueWeight, 2) * hueFactor;
            const distance = dl * dl + da * da + db * db + huePenalty;
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = i;
            }
        }
        return bestIndex;
    };

    const getPaletteCounts = (data, sampleStep) => {
        const counts = new Array(paletteWithLab.length).fill(0);
        let sampleCount = 0;
        for (let i = 0; i < data.length; i += 4 * sampleStep) {
            const index = getNearestPaletteIndex(
                data[i],
                data[i + 1],
                data[i + 2]
            );
            counts[index] += 1;
            sampleCount += 1;
        }
        return { counts, sampleCount };
    };

    const buildPaletteSelection = (counts, sampleCount, colorCount) => {
        const neutralMinCount = Math.max(
            1,
            Math.floor(sampleCount * neutralColorRatio)
        );
        const neutralIndices = neutralPaletteIndices.filter(
            (index) => counts[index] >= neutralMinCount
        );
        const nonNeutral = counts
            .map((count, index) => ({
                count,
                index,
                score: count * (1 + paletteWithLab[index].chroma / chromaScale)
            }))
            .filter(({ index }) => !neutralPaletteSet.has(index))
            .filter(({ count }) => count > 0)
            .sort((a, b) => b.score - a.score || b.count - a.count);
        const selectedNonNeutral = [];
        const binUsage = new Map();
        nonNeutral.forEach((entry) => {
            if (selectedNonNeutral.length >= colorCount) {
                return;
            }
            const hue = paletteWithLab[entry.index].hue;
            const bin = Math.floor(hue / hueBinSize);
            const used = binUsage.get(bin) || 0;
            if (used < maxPerHueBin) {
                selectedNonNeutral.push(entry.index);
                binUsage.set(bin, used + 1);
            }
        });
        if (selectedNonNeutral.length < colorCount) {
            nonNeutral.forEach((entry) => {
                if (
                    selectedNonNeutral.length < colorCount &&
                    !selectedNonNeutral.includes(entry.index)
                ) {
                    selectedNonNeutral.push(entry.index);
                }
            });
        }
        const selectedIndices = [...selectedNonNeutral, ...neutralIndices];
        const selectedColors = selectedIndices.map(
            (index) => paletteWithLab[index]
        );
        return { selectedColors, selectedNonNeutral };
    };

    const quantizeImage = (data, palette) => {
        if (!palette.length) {
            return data;
        }
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const bValue = data[i + 2];
            const [lValue, aValue, bLab] = rgbToLab(r, g, bValue);
            const chroma = Math.hypot(aValue, bLab);
            const hue = chroma < 0.5
                ? 0
                : (Math.atan2(bLab, aValue) * 180) / Math.PI + 360;
            const normalizedHue = hue % 360;
            const hueFactor = chroma / (chroma + hueDampen);
            let bestIndex = 0;
            let bestDistance = Number.POSITIVE_INFINITY;
            for (let j = 0; j < palette.length; j += 1) {
                const [pl, pa, pb] = palette[j].lab;
                const hueDiff = Math.min(
                    Math.abs(normalizedHue - palette[j].hue),
                    360 - Math.abs(normalizedHue - palette[j].hue)
                );
                const dl = (lValue - pl) * lightnessWeight;
                const da = aValue - pa;
                const db = bLab - pb;
                const huePenalty = Math.pow(hueDiff * hueWeight, 2) * hueFactor;
                const distance = dl * dl + da * da + db * db + huePenalty;
                if (distance < bestDistance) {
                    bestDistance = distance;
                    bestIndex = j;
                }
            }
            const [nr, ng, nb] = palette[bestIndex].rgb;
            data[i] = nr;
            data[i + 1] = ng;
            data[i + 2] = nb;
        }
        return data;
    };

    const handleGeneratePreview = async (
        sourceUrl = previewUrl,
        colorCount = previewColorCount
    ) => {
        if (!sourceUrl) {
            return;
        }
        previewRequestId.current += 1;
        const requestId = previewRequestId.current;
        setRenderStatus('processing');
        setRenderMessage(t('modal.options.preview.processing'));
        setRenderUrl('');
        setPaletteColors([]);
        renderBufferRef.current = null;
        setPaletteSelection(null);

        try {
            const image = await loadImage(sourceUrl);
            if (requestId !== previewRequestId.current) {
                return;
            }
            const { width: targetW, height: targetH } = getScaledSize(
                image.naturalWidth,
                image.naturalHeight,
                maxPreviewSize
            );
            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                setRenderStatus('error');
                setRenderMessage(preview.error);
                return;
            }
            ctx.drawImage(image, 0, 0, targetW, targetH);
            const imageData = ctx.getImageData(0, 0, targetW, targetH);
            const { data } = imageData;
            const pixelCount = targetW * targetH;
            const targetColors = Math.min(colorLimit, Math.max(2, colorCount));
            const sampleStep = Math.max(1, Math.floor(pixelCount / 4000));
            const { counts, sampleCount } = getPaletteCounts(data, sampleStep);
            const {
                selectedColors,
                selectedNonNeutral
            } = buildPaletteSelection(
                counts,
                sampleCount,
                targetColors
            );
            const {
                width: renderW,
                height: renderH
            } = getScaledSize(
                image.naturalWidth,
                image.naturalHeight,
                maxRenderSize
            );
            const renderCanvas = document.createElement('canvas');
            renderCanvas.width = renderW;
            renderCanvas.height = renderH;
            const renderCtx = renderCanvas.getContext('2d');
            if (!renderCtx) {
                setRenderStatus('error');
                setRenderMessage(preview.error);
                setPaletteColors([]);
                return;
            }
            renderCtx.drawImage(image, 0, 0, renderW, renderH);
            const renderData = renderCtx.getImageData(
                0,
                0,
                renderW,
                renderH
            );
            const resultData = quantizeImage(renderData.data, selectedColors);
            renderCtx.putImageData(
                new ImageData(resultData, renderW, renderH),
                0,
                0
            );

            if (requestId !== previewRequestId.current) {
                return;
            }
            const displayCount =
                selectedNonNeutral.length > 0
                    ? selectedNonNeutral.length
                    : selectedColors.length;
            renderBufferRef.current = {
                width: renderW,
                height: renderH,
                data: new Uint8ClampedArray(resultData)
            };
            setRenderUrl(renderCanvas.toDataURL('image/png'));
            setRenderStatus('success');
            setRenderMessage(
                t('modal.options.preview.success', { count: displayCount })
            );
            setPaletteColors(selectedColors);
        } catch (error) {
            if (requestId !== previewRequestId.current) {
                return;
            }
            setRenderStatus('error');
            setRenderMessage(preview.error);
            setPaletteColors([]);
            renderBufferRef.current = null;
        }
    };

    const calculatePrice = (widthValue, heightValue) => {
        const area = widthValue * heightValue;
        const maxArea = maxSize * maxSize;
        const areaPercentage = area / maxArea;

        const prices = Object.keys(unitPrices).map((key) => {
            let price = unitPrices[key] * areaPercentage;

            switch (key) {
                case 'Yarns':
                    price =
                        Math.ceil(areaPercentage * maxYarns) * unitPrices[key];
                    break;
                case 'Rug Glue':
                    price = (areaPercentage * unitPrices[key]) / 4;
                    break;
                case 'Anti-Slip Fabric':
                    price = (areaPercentage * unitPrices[key]) / 4;
                    break;
                case 'Spray Glue':
                    price = (areaPercentage * unitPrices[key]) / 4;
                    break;
                case 'Glue Gun':
                    price = unitPrices[key] * 4;
                    break;
                case 'Edge Cover Strip':
                    price = (areaPercentage * unitPrices[key]) / 4;
                    break;
                case 'Tufting Gun Costs':
                    price = unitPrices[key] * 0.08;
                    break;
                case 'Mower Costs':
                    price = unitPrices[key] * 0.1;
                    break;
                case 'Working Hours':
                    price =
                        Math.ceil(areaPercentage * maxHours) *
                        2 *
                        unitPrices[key];
                    break;
                default:
                    break;
            }

            return {
                desc: key,
                price: price
            };
        });

        let totalPrice = prices.reduce((sum, item) => sum + item.price, 0);
        if (isShipping) {
            totalPrice += 15;
        }
        if (isMiror) {
            totalPrice += 15;
        }

        return { prices, totalPrice };
    };

    const { prices, totalPrice } = calculatePrice(width, height);

    const optionRows = [
        ...(isWallMount
            ? [{ desc: options.wallMount.short, price: 0 }]
            : []),
        ...(isMiror ? [{ desc: options.mirror.short, price: 15 }] : []),
        ...(isShipping ? [{ desc: options.shipping.short, price: 15 }] : [])
    ];

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const isLocalhost =
                typeof window !== 'undefined' &&
                ['localhost', '127.0.0.1', '0.0.0.0'].includes(
                    window.location.hostname
                );

            if (isLocalhost) {
                await new Promise((resolve) => {
                    setTimeout(resolve, 500);
                });
            } else {
                const formData = new FormData(event.target);
                if (renderUrl) {
                    const renderBlob = dataUrlToBlob(renderUrl);
                    if (renderBlob) {
                        formData.append(
                            'renderImage',
                            renderBlob,
                            'render-preview.png'
                        );
                    }
                }
                const response = await fetch('/', {
                    method: 'POST',
                    body: formData
                });
                if (!response.ok) {
                    throw new Error('Submission failed');
                }
            }

            setSubmitStatus('success');
            setReferenceFile(null);
            setPreviewUrl((prev) => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }
                return '';
            });
            setRenderUrl('');
            setRenderStatus('idle');
            setRenderMessage('');
            setPaletteColors([]);
            renderBufferRef.current = null;
            event.target.reset();
            setPaletteSelection(null);
            setComment('');
            setShowComment(false);
            if (typeof onSuccess === 'function') {
                onSuccess(cta.success);
            }
            if (typeof onClose === 'function') {
                onClose();
            }
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getDragPayload = (event) => {
        const raw = event.dataTransfer.getData('application/json');
        if (raw) {
            try {
                return JSON.parse(raw);
            } catch (error) {
                return null;
            }
        }
        const hex = event.dataTransfer.getData('text/plain');
        return hex ? { hex, source: 'available' } : null;
    };

    const applyPaletteSwap = (fromColor, toColor) => {
        const buffer = renderBufferRef.current;
        if (!buffer) {
            handleGeneratePreview(previewUrl, previewColorCount);
            return;
        }
        const nextData = new Uint8ClampedArray(buffer.data);
        const [fromR, fromG, fromB] = fromColor.rgb;
        const [toR, toG, toB] = toColor.rgb;
        for (let i = 0; i < nextData.length; i += 4) {
            if (
                nextData[i] === fromR &&
                nextData[i + 1] === fromG &&
                nextData[i + 2] === fromB
            ) {
                nextData[i] = toR;
                nextData[i + 1] = toG;
                nextData[i + 2] = toB;
            }
        }
        const canvas = document.createElement('canvas');
        canvas.width = buffer.width;
        canvas.height = buffer.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        ctx.putImageData(
            new ImageData(nextData, buffer.width, buffer.height),
            0,
            0
        );
        renderBufferRef.current = {
            width: buffer.width,
            height: buffer.height,
            data: nextData
        };
        setRenderUrl(canvas.toDataURL('image/png'));
    };

    const applyPaletteSwapPair = (firstColor, secondColor) => {
        const buffer = renderBufferRef.current;
        if (!buffer) {
            return;
        }
        const nextData = new Uint8ClampedArray(buffer.data);
        const [firstR, firstG, firstB] = firstColor.rgb;
        const [secondR, secondG, secondB] = secondColor.rgb;
        for (let i = 0; i < nextData.length; i += 4) {
            const r = nextData[i];
            const g = nextData[i + 1];
            const bValue = nextData[i + 2];
            if (r === firstR && g === firstG && bValue === firstB) {
                nextData[i] = secondR;
                nextData[i + 1] = secondG;
                nextData[i + 2] = secondB;
            } else if (
                r === secondR &&
                g === secondG &&
                bValue === secondB
            ) {
                nextData[i] = firstR;
                nextData[i + 1] = firstG;
                nextData[i + 2] = firstB;
            }
        }
        const canvas = document.createElement('canvas');
        canvas.width = buffer.width;
        canvas.height = buffer.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        ctx.putImageData(
            new ImageData(nextData, buffer.width, buffer.height),
            0,
            0
        );
        renderBufferRef.current = {
            width: buffer.width,
            height: buffer.height,
            data: nextData
        };
        setRenderUrl(canvas.toDataURL('image/png'));
    };

    const handlePaletteSwapIndices = (firstIndex, secondIndex) => {
        if (firstIndex === secondIndex) {
            return;
        }
        const firstColor = paletteColors[firstIndex];
        const secondColor = paletteColors[secondIndex];
        if (!firstColor || !secondColor) {
            return;
        }
        const nextPalette = [...paletteColors];
        nextPalette[firstIndex] = secondColor;
        nextPalette[secondIndex] = firstColor;
        setPaletteColors(nextPalette);
        applyPaletteSwapPair(firstColor, secondColor);
    };

    const handlePaletteReplace = (targetIndex, nextColor) => {
        if (!paletteColors.length || !nextColor) {
            return;
        }
        const existingIndex = paletteColors.findIndex(
            (color) => color.hex === nextColor.hex
        );
        if (existingIndex === targetIndex) {
            return;
        }
        if (existingIndex !== -1) {
            return;
        }
        const previousColor = paletteColors[targetIndex];
        if (!previousColor) {
            return;
        }
        const nextPalette = paletteColors.map((color, index) =>
            index === targetIndex ? nextColor : color
        );
        setPaletteColors(nextPalette);
        applyPaletteSwap(previousColor, nextColor);
    };

    const handleAvailableDragStart = (event, color) => {
        event.dataTransfer.setData(
            'application/json',
            JSON.stringify({ hex: color.hex, source: 'available' })
        );
        event.dataTransfer.setData('text/plain', color.hex);
    };

    const handleUsedDragStart = (event, color, index) => {
        event.dataTransfer.setData(
            'application/json',
            JSON.stringify({ hex: color.hex, source: 'used', index })
        );
        event.dataTransfer.setData('text/plain', color.hex);
    };

    const handlePaletteDrop = (event, targetIndex) => {
        event.preventDefault();
        const payload = getDragPayload(event);
        if (!payload || !payload.hex) {
            return;
        }
        const nextColor = paletteWithLab.find(
            (color) => color.hex === payload.hex
        );
        if (!nextColor) {
            return;
        }
        if (payload.source === 'used') {
            const sourceIndex = Number(payload.index);
            if (Number.isNaN(sourceIndex) || sourceIndex === targetIndex) {
                return;
            }
            const sourceColor = paletteColors[sourceIndex];
            const targetColor = paletteColors[targetIndex];
            if (!sourceColor || !targetColor) {
                return;
            }
            const nextPalette = [...paletteColors];
            nextPalette[targetIndex] = sourceColor;
            nextPalette[sourceIndex] = targetColor;
            setPaletteColors(nextPalette);
            applyPaletteSwapPair(sourceColor, targetColor);
            return;
        }
        handlePaletteReplace(targetIndex, nextColor);
    };

    const handleUsedSwatchClick = (index) => {
        if (!paletteColors[index]) {
            return;
        }
        if (!paletteSelection) {
            setPaletteSelection({ source: 'used', index });
            return;
        }
        if (paletteSelection.source === 'used') {
            if (paletteSelection.index === index) {
                setPaletteSelection(null);
                return;
            }
            handlePaletteSwapIndices(paletteSelection.index, index);
            setPaletteSelection(null);
            return;
        }
        if (paletteSelection.source === 'available') {
            const nextColor = paletteWithLab.find(
                (color) => color.hex === paletteSelection.hex
            );
            if (!nextColor) {
                setPaletteSelection(null);
                return;
            }
            handlePaletteReplace(index, nextColor);
            setPaletteSelection(null);
        }
    };

    const handleAvailableSwatchClick = (hex) => {
        if (!hex) {
            return;
        }
        if (!paletteSelection) {
            setPaletteSelection({ source: 'available', hex });
            return;
        }
        if (paletteSelection.source === 'available') {
            if (paletteSelection.hex === hex) {
                setPaletteSelection(null);
            } else {
                setPaletteSelection({ source: 'available', hex });
            }
            return;
        }
        if (paletteSelection.source === 'used') {
            const nextColor = paletteWithLab.find((color) => color.hex === hex);
            if (!nextColor) {
                setPaletteSelection(null);
                return;
            }
            handlePaletteReplace(paletteSelection.index, nextColor);
            setPaletteSelection(null);
        }
    };

    useEffect(() => {
        if (open) {
            setSubmitStatus('idle');
            setIsSubmitting(false);
            setReferenceFile(null);
            setPreviewUrl((prev) => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }
                return '';
            });
            setRenderUrl('');
            setRenderStatus('idle');
            setRenderMessage('');
            setPaletteColors([]);
            renderBufferRef.current = null;
            setEmail('');
            setPaletteSelection(null);
            setComment('');
            setShowComment(false);
        }
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby='tufted-modal-title'
            aria-describedby='tufted-modal-description'
            BackdropProps={{
                sx: { backgroundColor: 'rgba(16, 20, 28, 0.6)' }
            }}
        >
            <form
                className='tufted-modal'
                name={formName}
                method='POST'
                data-netlify='true'
                netlify-honeypot='bot-field'
                encType='multipart/form-data'
                onSubmit={handleSubmit}
            >
                <input type='hidden' name='form-name' value={formName} />
                <input type='hidden' name='name' value={subjectLine} />
                <input type='hidden' name='email' value={email} />
                <input type='hidden' name='language' value={language} />
                <input type='hidden' name='subject' value={subjectLine} />
                <input type='hidden' name='width' value={width} />
                <input type='hidden' name='height' value={height} />
                <input
                    type='hidden'
                    name='wallMount'
                    value={isWallMount ? 'yes' : 'no'}
                />
                <input
                    type='hidden'
                    name='mirror'
                    value={isMiror ? 'yes' : 'no'}
                />
                <input
                    type='hidden'
                    name='shipping'
                    value={isShipping ? 'yes' : 'no'}
                />
                <input
                    type='hidden'
                    name='estimateTotal'
                    value={totalPrice.toFixed(2)}
                />
                <input type='hidden' name='comment' value={comment} />
                <input
                    type='hidden'
                    name='paletteColors'
                    value={paletteSummary}
                />
                <div style={{ display: 'none' }}>
                    <label>
                        Do not fill this out
                        <input name='bot-field' />
                    </label>
                </div>
                <div className='tufted-modal-header'>
                    <div>
                        <p className='eyebrow'>{t('modal.eyebrow')}</p>
                        <h2 id='tufted-modal-title'>
                            {t('modal.title')}
                        </h2>
                        <p
                            id='tufted-modal-description'
                            className='tufted-modal-lead'
                        >
                            {t('modal.lead', { max: maxSize })}
                        </p>
                    </div>
                    <button
                        type='button'
                        className='modal-close'
                        onClick={onClose}
                        aria-label={t('modal.close')}
                        data-track-label='Fermer modal tufting'
                    >
                        <XIcon size={16} weight='regular' />
                    </button>
                </div>

                <div className='tufted-modal-grid'>
                    <div className='tufted-modal-left'>
                        <div className='card tufted-panel'>
                            <div className='panel-header'>
                                <span className='chip'>{dimensions.chip}</span>
                                <span className='panel-note'>
                                    {dimensions.note}
                                </span>
                            </div>
                            <div className='slider-stack'>
                                <div className='slider-card'>
                                    <div className='slider-label'>
                                        <span>{dimensions.width}</span>
                                        <span className='value-pill'>
                                            {width} cm
                                        </span>
                                    </div>
                                    <Slider
                                        value={width}
                                        onChange={handleWidthChange}
                                        aria-labelledby={dimensions.width}
                                        min={0}
                                        max={maxSize}
                                        valueLabelDisplay='off'
                                        sx={sliderSx}
                                    />
                                </div>
                                <div className='slider-card'>
                                    <div className='slider-label'>
                                        <span>{dimensions.height}</span>
                                        <span className='value-pill'>
                                            {height} cm
                                        </span>
                                    </div>
                                    <Slider
                                        value={height}
                                        onChange={handleHeightChange}
                                        aria-labelledby={dimensions.height}
                                        min={0}
                                        max={maxSize}
                                        valueLabelDisplay='off'
                                        sx={sliderSx}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='card tufted-panel'>
                            <div className='panel-header'>
                                <span className='chip'>{options.chip}</span>
                                <span className='panel-note'>
                                    {options.note}
                                </span>
                            </div>
                            <label className='option-row'>
                                <Checkbox
                                    size='small'
                                    checked={isWallMount}
                                    onChange={handleWallMountChange}
                                    sx={checkboxSx}
                                />
                                <span>{options.wallMount.label}</span>
                            </label>
                            <label className='option-row'>
                                <Checkbox
                                    size='small'
                                    checked={isMiror}
                                    onChange={handleMirorChange}
                                    sx={checkboxSx}
                                />
                                <span>{options.mirror.label}</span>
                            </label>
                            <label className='option-row'>
                                <Checkbox
                                    size='small'
                                    checked={isShipping}
                                    onChange={handleShippingChange}
                                    sx={checkboxSx}
                                />
                                <span>{options.shipping.label}</span>
                            </label>
                        </div>
                    </div>

                    <div className='tufted-modal-right'>
                        <div className='card tufted-panel tufted-panel-breakdown'>
                            <div className='panel-header'>
                                <span className='chip'>{breakdown.chip}</span>
                                <span className='panel-note'>
                                    {breakdown.note}
                                </span>
                            </div>
                            <div className='price-list'>
                                {prices.map((row) => (
                                    <div key={row.desc} className='price-row'>
                                        <span>
                                            {priceLabels[row.desc] || row.desc}
                                        </span>
                                        <span>{row.price.toFixed(2)}€</span>
                                    </div>
                                ))}
                                {optionRows.map((row) => (
                                    <div
                                        key={row.desc}
                                        className='price-row option'
                                    >
                                        <span>{row.desc}</span>
                                        <span>{row.price.toFixed(2)}€</span>
                                    </div>
                                ))}
                            </div>
                            <div className='total-row'>
                                <span>{breakdown.total}</span>
                                <span className='total-price'>
                                    {totalPrice.toFixed(2)}€
                                </span>
                            </div>
                            <p className='modal-footnote'>
                                {breakdown.footnote}
                            </p>
                        </div>

                    </div>
                </div>

                <div className='card tufted-panel tufted-panel-upload'>
                    <div className='panel-header'>
                        <span className='chip'>
                            {options.reference.label}
                        </span>
                        <span className='panel-note'>
                            {options.reference.note}
                        </span>
                    </div>
                    <div className='option-upload'>
                        <label className='file-input'>
                            <input
                                type='file'
                                accept='image/*'
                                name='referenceImage'
                                onChange={handleReferenceChange}
                            />
                            <span>
                                {referenceFile
                                    ? referenceFile.name
                                    : options.reference.placeholder}
                            </span>
                        </label>
                        {previewUrl ? (
                            <div className='render-controls'>
                                <div className='render-colors slider-card'>
                                    <div className='render-colors-header'>
                                        <span>{preview.colorsLabel}</span>
                                        <span className='value-pill'>
                                            {previewColorCount}
                                        </span>
                                    </div>
                                    <Slider
                                        value={previewColorCount}
                                        min={2}
                                        max={colorLimit}
                                        step={1}
                                        onChange={handlePreviewColorChange}
                                        valueLabelDisplay='off'
                                        sx={sliderSx}
                                    />
                                </div>
                            </div>
                        ) : null}
                        {renderMessage ? (
                            <p
                                className={`render-message ${renderStatus}`}
                            >
                                <span>{renderMessage}</span>
                                {renderStatus === 'success' ? (
                                    <span className='render-note'>
                                        <WarningCircleIcon
                                            size={14}
                                            weight='regular'
                                        />
                                        {preview.note}
                                    </span>
                                ) : null}
                            </p>
                        ) : null}
                        {previewUrl ? (
                            <div className='render-grid'>
                                <div className='render-card'>
                                    <span>{preview.original}</span>
                                    <img
                                        src={previewUrl}
                                        alt={preview.original}
                                    />
                                </div>
                                {renderUrl ? (
                                    <div className='render-card'>
                                        <span>{preview.rendered}</span>
                                        <img
                                            src={renderUrl}
                                            alt={preview.rendered}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {paletteColors.length > 0 ? (
                            <div className='render-palette'>
                                <span>{preview.palette}</span>
                                <div className='palette-swatches'>
                                    {paletteColors.map((color, index) => (
                                        <span
                                            key={`${color.name}-${color.hex}`}
                                            className={`palette-swatch palette-swatch-used${
                                                paletteSelection?.source ===
                                                    'used' &&
                                                paletteSelection.index === index
                                                    ? ' palette-swatch-selected'
                                                    : ''
                                            }`}
                                            style={{
                                                backgroundColor: color.hex
                                            }}
                                            aria-label={`${color.name} ${color.hex}`}
                                            title={`${color.name} ${color.hex}`}
                                            draggable
                                            onDragStart={(event) =>
                                                handleUsedDragStart(
                                                    event,
                                                    color,
                                                    index
                                                )
                                            }
                                            onClick={() =>
                                                handleUsedSwatchClick(index)
                                            }
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                            }}
                                            onDrop={(event) =>
                                                handlePaletteDrop(event, index)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        {paletteColors.length > 0 ? (
                            <div className='render-palette'>
                                <span>{preview.paletteAvailable}</span>
                                <div className='palette-swatches'>
                                    {availablePalette.map((color) => (
                                        <span
                                            key={`${color.name}-${color.hex}`}
                                            className={`palette-swatch palette-swatch-available${
                                                paletteSelection?.source ===
                                                    'available' &&
                                                paletteSelection.hex === color.hex
                                                    ? ' palette-swatch-selected'
                                                    : ''
                                            }`}
                                            style={{
                                                backgroundColor: color.hex
                                            }}
                                            aria-label={`${color.name} ${color.hex}`}
                                            title={`${color.name} ${color.hex}`}
                                            draggable
                                            onDragStart={(event) =>
                                                handleAvailableDragStart(
                                                    event,
                                                    color
                                                )
                                            }
                                            onClick={() =>
                                                handleAvailableSwatchClick(
                                                    color.hex
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className='tufted-modal-cta'>
                    <div>
                        <h3>{cta.title}</h3>
                        <label className='cta-label'>
                            <span>{cta.emailLabel}</span>
                            <span className='cta-note'>{cta.note}</span>
                            <input
                                type='email'
                                value={email}
                                onChange={handleEmailChange}
                                placeholder={cta.emailPlaceholder}
                                autoComplete='email'
                                required
                            />
                        </label>
                        <button
                            type='button'
                            className='button button-outline cta-comment-toggle'
                            onClick={() => setShowComment((prev) => !prev)}
                        >
                            {showComment
                                ? cta.commentHide
                                : cta.commentShow}
                        </button>
                        {showComment ? (
                            <label className='cta-label cta-comment'>
                                <span>{cta.commentLabel}</span>
                                <textarea
                                    value={comment}
                                    onChange={handleCommentChange}
                                    placeholder={cta.commentPlaceholder}
                                    rows={3}
                                />
                            </label>
                        ) : null}
                        {submitStatus === 'success' ? (
                            <p className='form-status success'>
                                {cta.success}
                            </p>
                        ) : null}
                        {submitStatus === 'error' ? (
                            <p className='form-status error'>
                                {cta.error}
                            </p>
                        ) : null}
                    </div>
                    <button
                        type='submit'
                        className='button'
                        disabled={isSubmitting || !isEmailValid}
                        aria-busy={isSubmitting}
                    >
                        <EnvelopeSimpleIcon size={16} weight='regular' />
                        {isSubmitting
                            ? cta.sending
                            : cta.submit}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default TuftedRugModal;
